// @flow

import React, {Component} from 'react';
import {Tree, mapChildrenToParent} from '@pb/kit-component-tree-v2';
import {shouldUpdate} from '@pb/kit-util-should-update';

// .json for correct module resolution in TS
import {layoutLevels} from '@pb/kit-stylus/config.json';

import {CheckboxTreeNode} from './checkbox-tree-node.component';
import {CheckedTextConfig} from './constants';
import {
    flattenTreeByIdsList,
    getCheckedTextList,
    getCheckedTextListUniq,
    getAllCheckedTextList,
    getCheckedNestedPathList,
    parseCheckedIds,
    checkParent,
    getDisabledNode
} from './utils';

import type {TreeStructure, ExpandedTree, NodeProps, PrefixType} from '@pb/kit-component-tree-v2';

import type {CheckedTextConfigType} from './constants';
import type {CheckedTree, ChangeTreeType, ViewType, GetCheckedTextList} from './typedef';

/**
 * Checkbox Tree props
 */
type Props = {|
    /**
     * Data to build the tree
     */
    data: TreeStructure,
    /**
     * Checked item ids
     */
    checkedIdsList: Array<string>,
    /**
     * Expanded tree node ids
     */
    expandedIdsTree: ExpandedTree,
    /**
     * Component instance name
     */
    name: string,
    /**
     * Callback called if data changed
     */
    onChange: (changeTreeType: ChangeTreeType) => void,
    /**
     * Disabled state
     */
    disabled?: boolean,
    /**
     * No Empty tree
     */
    notEmpty?: boolean,
    /**
     * Type of prefix ID or NUMERATION
     */
    prefixType?: PrefixType,
    /**
     * Component layout level (Layout priority).
     */
    layoutLevel?: $Values<typeof layoutLevels>,
    /**
     * Show explanation popovers
     */
    showExplanations?: boolean,
    /**
     * Function return an item and provides an interface to work with them
     */
    view?: ViewType,
    /**
     * If true - the switch will not change its state by click, only the parent onChange will be called
     */
    usePropsOnly: boolean,
    checkedTextConfig: CheckedTextConfigType,
|};

type State = {|
    checkedIdsList: Array<string>,
    expandedIdsTree: ExpandedTree,
    checkedIdsTree: CheckedTree,
    disabled: boolean,
    disabledNode: ?string,
    mappedChildrenToParent: {[key: string]: Array<string> },
|};

type UpdatesType = {
    checkedId?: string,
    checkedIdsTree?: CheckedTree,
    expandedIdsTree?: ExpandedTree,
    checkedIdsList?: Array<string>,
};

export const CheckedTextUtil: { [CheckedTextConfigType]: GetCheckedTextList } = {
    [CheckedTextConfig.WITHOUT_CHILDREN]: getCheckedTextList,
    [CheckedTextConfig.WITHOUT_CHILDREN_UNIQ]: getCheckedTextListUniq,
    [CheckedTextConfig.NESTED_PATH]: getCheckedNestedPathList,
    [CheckedTextConfig.ALL_CAPTIONS]: getAllCheckedTextList,
};

export class CheckboxTree extends Component<Props, State> {
    static defaultProps = {
        checkedIdsList: [],
        expandedIdsTree: {},
        name: '',
        notEmpty: false,
        showExplanations: false,
        layoutLevel: layoutLevels.level8,
        usePropsOnly: false,
        checkedTextConfig: CheckedTextConfig.WITHOUT_CHILDREN
    };

    constructor(props: Props) {
        super(props);
        const {data, checkedIdsList, expandedIdsTree, notEmpty} = props;
        this.state = {
            disabledNode: getDisabledNode(checkedIdsList, notEmpty),
            disabled: props.disabled || false,
            checkedIdsList,
            expandedIdsTree,
            checkedIdsTree: parseCheckedIds(data, checkedIdsList),
            mappedChildrenToParent: mapChildrenToParent(data)
        };
    }

    componentDidUpdate(prevProps: Props) {
        const {data, checkedIdsList, notEmpty, expandedIdsTree, disabled} = this.props;
        const isDataChanged = shouldUpdate(this.props, prevProps, ['data']);
        const isCheckedIdsListChanged = shouldUpdate(this.props, prevProps, ['checkedIdsList']);

        if (isDataChanged) {
            this.setState({
                mappedChildrenToParent: mapChildrenToParent(data)
            });
        }

        if (isCheckedIdsListChanged) {
            this.setState({checkedIdsList});
        }

        if (isDataChanged || isCheckedIdsListChanged) {
            this.setState({
                checkedIdsTree: parseCheckedIds(data, checkedIdsList)
            });
        }

        if (shouldUpdate(this.props, prevProps, ['expandedIdsTree', 'disabled'])) {
            this.setState({expandedIdsTree, disabled});
        }

        if (isCheckedIdsListChanged || shouldUpdate(this.props, prevProps, ['notEmpty'])) {
            this.setState({
                disabledNode: getDisabledNode(checkedIdsList, notEmpty)
            });
        }
    }

    runOnChange(updates: UpdatesType) {
        const {name, data, notEmpty, usePropsOnly, checkedTextConfig, onChange} = this.props;
        const {
            checkedId,
            checkedIdsTree = this.state.checkedIdsTree,
            expandedIdsTree = this.state.expandedIdsTree,
            checkedIdsList = this.state.checkedIdsList
        } = updates;

        let updatedCheckedIdsTree = checkedIdsTree;
        let updatedCheckedIdsList = checkedIdsList;
        let disabledNode = null;

        if (notEmpty) {
            const {checkedIdsList: currentCheckedIdsList} = this.props;

            if (!checkedIdsList.length) {
                updatedCheckedIdsList = flattenTreeByIdsList(data, currentCheckedIdsList)
                    .updatedCheckedIdsList
                    .filter((id, i) => i === 0);

                updatedCheckedIdsTree = parseCheckedIds(data, updatedCheckedIdsList);

                disabledNode = updatedCheckedIdsList[0];
            }
        }

        const updatedCheckedCaptionsList = CheckedTextUtil[checkedTextConfig](data, updatedCheckedIdsList);

        onChange?.({
            name,
            checkedId,
            checkedIdsList: updatedCheckedIdsList,
            checkedText: updatedCheckedCaptionsList.join(', '),
            expandedIdsTree
        });

        if (usePropsOnly) {
            this.setState({expandedIdsTree});
        } else {
            this.setState({
                checkedIdsTree: updatedCheckedIdsTree,
                checkedIdsList: updatedCheckedIdsList,
                expandedIdsTree,
                disabledNode
            });
        }
    }

    onChange = (currentCheckedIdsList: Array<string>, id: string, checked: boolean) => {
        const {mappedChildrenToParent} = this.state;
        const {data} = this.props;

        const parentId = mappedChildrenToParent[id][0];
        const checkedIdsList = checkParent({checkedIdsList: currentCheckedIdsList, id, parentId, checked, data});

        this.runOnChange({checkedIdsTree: parseCheckedIds(data, checkedIdsList), checkedIdsList, checkedId: id});
    };

    updateExpandedIds = (expandedIdsTree: ExpandedTree) => {
        this.runOnChange({expandedIdsTree});
    };

    getTreeNode = (nodeProps: NodeProps) => {
        const {checkedIdsTree, checkedIdsList, disabledNode} = this.state;
        const {layoutLevel, name, showExplanations, view} = this.props;

        return (
            <CheckboxTreeNode
                {...{
                    name,
                    view,
                    disabledNode,
                    checkedIdsTree,
                    checkedIdsList,
                    layoutLevel,
                    showExplanations,
                    onChange: this.onChange,
                    ...nodeProps
                }}
            />
        );
    };

    render() {
        const {disabled, expandedIdsTree} = this.state;
        const {data, prefixType} = this.props;

        return (
            <Tree
                data={data}
                updateExpandedIds={this.updateExpandedIds}
                disabled={disabled}
                prefixType={prefixType}
                expandedIdsTree={expandedIdsTree}
                getNode={this.getTreeNode}
            />
        );
    }
}
